import type { User } from '../../scenes/authentication/types/types.auth';
import HomeIcon from '@mui/icons-material/Home';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import SettingsIcon from '@mui/icons-material/Settings';
import {
  Avatar,
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';
import * as Sentry from '@sentry/react';
import NavbarCompanySearch from '@shared/components/NavbarCompanySearch';
import UserAvatar from '@shared/components/UserAvatar';
import { useResponsive } from '@shared/hooks/useResponsive';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import kandirLogo from '../../assets/images/kandir_logo.png';
import SignUpOrLogInModal from '../../scenes/authentication/components/SignUpOrLogInModal';
import { useReviewContext } from '../../scenes/review/context/ReviewContext';
import { UserContext } from '../contexts/UserContext';
import SideDrawer, { shouldHideDrawer } from './SideDrawer';

export interface NavBarProps {
  isNavOpen: boolean;
  setIsNavOpen: (isOpen: boolean) => void;
  transparent: boolean;
}

function Navbar(props: NavBarProps) {
  const { setIsNavOpen, isNavOpen } = props;
  const { user, logout } = useContext(UserContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile } = useResponsive();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authModalMode, setAuthModalMode] = useState<'signup' | 'login'>('signup');

  // Check if review dialog is open
  let isReviewDialogOpen = false;
  try {
    const reviewContext = useReviewContext();
    isReviewDialogOpen = reviewContext.isReviewDialogOpen;
  }
  catch {
    // ReviewContext not available, no review dialog open
    isReviewDialogOpen = false;
  }

  const isOnboarding = location.pathname === '/onboarding';

  useEffect(() => {
    setAnchorEl(null);
  }, [user]);

  const handleAccountClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleGoHome = () => {
    navigate('/my-reviews');
    handleMenuClose();
  };

  const handleGoSettings = () => {
    navigate('/account-settings');
    handleMenuClose();
  };

  const handleLogout = () => {
    handleMenuClose();
    logout();
  };

  const handleFeedback = useCallback(async () => {
    const feedback = Sentry.getFeedback();
    const form = await feedback?.createForm();
    if (form) {
      form.appendToDom();
      form.open();
    }
  }, []);

  const handleSignUpClick = () => {
    sessionStorage.setItem('preLoginPath', location.pathname + location.search);
    setAuthModalMode('signup');
    setAuthModalOpen(true);
  };

  const handleLogInClick = () => {
    sessionStorage.setItem('preLoginPath', location.pathname + location.search);
    setAuthModalMode('login');
    setAuthModalOpen(true);
  };

  const handleAuthModalClose = () => {
    setAuthModalOpen(false);
  };

  const handleAuthSuccess = () => {
    const storedPath = sessionStorage.getItem('preLoginPath');
    const redirectPath = storedPath || '/my-reviews';
    sessionStorage.removeItem('preLoginPath');
    setAuthModalOpen(false);
    navigate(redirectPath);
  };

  const hideDrawer = shouldHideDrawer(location.pathname);

  return (
    <>
      <Box role="app-nav" className="navbar">
        <IconButton
          onClick={() => setIsNavOpen(!isNavOpen)}
          className="navbar__menu-button"
          sx={{
            display: isNavOpen || hideDrawer ? 'none' : 'flex',
          }}
        >
          <MenuIcon fontSize="large" />
        </IconButton>

        {(!isNavOpen || hideDrawer) && !isMobile && (
          <Box
            component="img"
            src={kandirLogo}
            alt="Kandir Logo"
            className="navbar__logo"
          />
        )}

        <Box className="navbar__right-section">
          {!isMobile && !isOnboarding && (
            <Box
              className="navbar__search-container"
              sx={{ display: isReviewDialogOpen ? 'none' : 'block' }}
            >
              <NavbarCompanySearch />
            </Box>
          )}

          {user
            ? (
                <>
                  {!isMobile && !isOnboarding && !isReviewDialogOpen && (
                    <Button
                      variant="contained"
                      onClick={handleFeedback}
                      className="button-rounded button-rounded--solid-white"
                    >
                      Give Feedback
                    </Button>
                  )}

                  {!isOnboarding && !isReviewDialogOpen && (
                    <Tooltip title={`Logged in as ${user.email}`}>
                      <IconButton
                        onClick={handleAccountClick}
                        className="navbar__avatar-button"
                      >
                        <UserAvatar
                          firstName={user.firstName}
                          lastName={user.lastName}
                          username={user.username}
                          avatarColor={user.avatarColor}
                          size={40}
                        />
                      </IconButton>
                    </Tooltip>
                  )}

                  <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleMenuClose}
                    slotProps={{
                      paper: {
                        className: 'navbar-menu__paper',
                      },
                      list: {
                        className: 'navbar-menu__list',
                      },
                    }}
                  >
                    <MenuItem onClick={handleGoHome} className="navbar-menu__item">
                      <HomeIcon className="navbar-menu__icon" />
                      Home
                    </MenuItem>
                    <MenuItem onClick={handleGoSettings} className="navbar-menu__item">
                      <SettingsIcon className="navbar-menu__icon" />
                      Settings
                    </MenuItem>
                    <MenuItem onClick={handleLogout} className="navbar-menu__item navbar-menu__item--logout">
                      <LogoutIcon className="navbar-menu__icon" />
                      Logout
                    </MenuItem>
                  </Menu>
                </>
              )
            : (
                <Box className="navbar__auth-buttons">
                  <Button
                    variant="contained"
                    onClick={handleLogInClick}
                    className="button-rounded button-rounded--solid-white"
                  >
                    Log In
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleSignUpClick}
                    className="button-rounded button-rounded--primary"
                  >
                    Sign Up
                  </Button>
                </Box>
              )}
        </Box>
      </Box>

      <SideDrawer isOpen={isNavOpen} setIsOpen={setIsNavOpen} />

      <SignUpOrLogInModal
        open={authModalOpen}
        onClose={handleAuthModalClose}
        initialMode={authModalMode}
        handleAuthSuccess={handleAuthSuccess}
      />
    </>
  );
}

export default Navbar;
